import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, DatePicker, Divider, Form, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as CommentIcon } from '../../../../../assets/svg/comment-dots-solid.svg';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  REQUEST_TYPES,
  ROUTES,
  SORT,
} from '../../../../../common/constants';
import { formatDate, hasPermission } from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import { UPDATE_REQUEST_ADMIN } from '../../../graphql/Mutations';
import { SUGGESTED_TOY_REQUESTS_ADMIN } from '../graphql/Queries';
import NoteModal from './NoteModal';

const { RangePicker } = DatePicker;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialSuggestedToyFilter = {
  limit: 10,
  search: '',
  skip: 0,
};

const initialSuggestedToySort = {
  field: 'createdAt',
  order: 'DESC',
};

const SuggestedToyTable = ({ exportFilter, setExportFilter }) => {
  const {
    state: {
      permissions,
      commonPermissions: { isUserPermission },
    },
  } = useContext(AppContext);
  const history = useHistory();
  const [form] = Form.useForm();
  const [suggestedToyList, setSuggestedToyList] = useState([]);
  const [suggestedToySearchTerm, setSuggestedToySearchTerm] = useState('');
  const [isEmptySuggestedToyList, setIsEmptySuggestedToyList] = useState(false);
  const [suggestedToy, setSuggestedToy] = useState({});
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isNoteLoading, setIsNoteLoading] = useState(false);
  const [isNotePrompts, setIsNotePrompts] = useState(false);

  const [isSuggestedToyLoading, setIsSuggestedToyLoading] = useState(true);
  const [whereFilter, setWhereFilter] = useState({});
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [finalWhereFilters, setFinalWhereFilters] = useState({});
  const [finalFilterTags, setFinalFilterTags] = useState(null);

  const [requestsAdmin] = useLazyQuery(SUGGESTED_TOY_REQUESTS_ADMIN, {
    onCompleted: (response) => {
      setSuggestedToyList([...response?.requestsAdmin?.data]);
      if (
        response?.requestsAdmin?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptySuggestedToyList(true);
      } else {
        setIsEmptySuggestedToyList(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.requestsAdmin?.count,
      };
      setPaginationProp(pagination);
      setIsSuggestedToyLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [updateRequestAdmin] = useMutation(UPDATE_REQUEST_ADMIN, {
    onError: () => {},
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.REQUESTS_END_USER_SUGGESTED_TOY,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsSuggestedToyLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          type: REQUEST_TYPES?.TOY_SUGGESTION,
        },
        sort: initialSuggestedToySort,
      },
    });
  }, []);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsSuggestedToyLoading(true);
    setSortedInfo(sorter);
    requestsAdmin({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          skip,
          limit: pagination?.pageSize,
          search: suggestedToySearchTerm,
          type: REQUEST_TYPES?.TOY_SUGGESTION,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setSuggestedToySearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsSuggestedToyLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          type: REQUEST_TYPES?.TOY_SUGGESTION,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleAddNote = async (values) => {
    setIsNoteLoading(true);
    const response = await updateRequestAdmin({
      variables: {
        where: {
          id: suggestedToy?.id,
        },
        data: {
          suggestToy: {
            notes: values?.note,
          },
        },
      },
    });
    if (response?.data) {
      setIsSuggestedToyLoading(true);
      setIsNotePrompts(false);
      form?.resetFields();
      requestsAdmin({
        variables: {
          filter: {
            ...initialSuggestedToyFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: suggestedToySearchTerm,
            type: REQUEST_TYPES?.TOY_SUGGESTION,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialSuggestedToySort,
        },
      });
    }
    setIsNoteLoading(false);
  };

  const getFilterData = (confirm) => {
    setIsSuggestedToyLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: suggestedToySearchTerm,
          type: REQUEST_TYPES?.TOY_SUGGESTION,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setExportFilter({ ...exportFilter, ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setExportFilter({
      ...exportFilter,
      ...finalWhereFilters,
      ...whereFiltersCopy,
    });
    setIsSuggestedToyLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: suggestedToySearchTerm,
          type: REQUEST_TYPES?.TOY_SUGGESTION,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      if (!filterVisible) {
        setFilterTags(finalFilterTags);
        setWhereFilter(finalWhereFilters);
      }
    }
  }, [filterVisible]);

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterVisible(visible);
    },
  });

  const columns = [
    {
      title: 'USER NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: true,
      width: 300,
      align: 'left',
      className: `max-width-column`,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
      onCell: (record) => ({
        onClick: () =>
          isUserPermission &&
          record?.createdBy?.id &&
          history?.push(
            `${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}${ROUTES?.BASIC_DETAILS}/view/${record?.createdBy?.id}/${record?.createdBy?.userNumber}`,
          ),
        className: `${
          isUserPermission && record?.createdBy?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const userData = record?.createdBy;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{`${
                userData?.firstName ? userData?.firstName : ''
              } ${userData?.lastName ? userData?.lastName : ''}`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'REQUEST ID',
      dataIndex: 'requestNumber',
      key: 'requestNumber',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.requestNumber,
    },

    {
      title: 'REQUEST DATE',
      dataIndex: 'requestDate',
      key: 'requestDate',
      ellipsis: true,
      width: 300,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 300,
      align: 'left',
      className: 'max-width-column table-tooltip-cell',
      render: (_, record) => (
        <Tooltip
          title={record?.description ? record?.description : 'No Description'}
          placement="topLeft"
          trigger="click"
          className="pointer"
        >
          {record?.description || '-'}
        </Tooltip>
      ),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 100,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      render: (_, record) => {
        let tooltipTitle = '';
        if (record?.metaData?.notes) {
          tooltipTitle = 'View Note';
        } else if (!hasUpdatePermission) {
          tooltipTitle = 'No Note';
        } else {
          tooltipTitle = 'Add Note';
        }
        return (
          <>
            <div className="action-button">
              <div className="mr-16 d-flex">
                <Tooltip
                  title={tooltipTitle}
                  placement="top"
                  className="table-action-button"
                  zIndex={4}
                >
                  <Button
                    type="link"
                    onClick={(e) => {
                      e?.stopPropagation();
                      setIsNotePrompts(true);
                      setSuggestedToy(record);
                    }}
                    disabled={!record?.metaData?.notes && !hasUpdatePermission}
                  >
                    <CommentIcon
                      className={
                        record?.metaData?.notes
                          ? 'pointer svg-icon'
                          : 'pointer svg-icon-grey'
                      }
                    />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </>
        );
      },
    },
  ];

  const locale = {
    emptyText: isEmptySuggestedToyList ? '' : <span />,
  };

  return (
    <>
      <NoteModal
        form={form}
        note={suggestedToy?.metaData?.notes}
        isModalOpen={isNotePrompts}
        setIsModalOpen={setIsNotePrompts}
        isBtnLoading={isNoteLoading}
        onFinish={handleAddNote}
      />
      <div className="d-flex justify-end mb-16 flex-wrap">
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={suggestedToyList || []}
        loading={isSuggestedToyLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default SuggestedToyTable;
